import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { setAuthToken } from "@/lib/api";
import { recordSessionLogin } from "@/lib/store";
import { ShieldCheck, Check, X, Eye, EyeOff } from "lucide-react";

export default function StaffLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property") || "";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Mandatory Credential Upgrade State
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [tempUser, setTempUser] = useState(null);

  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < 8) return "Password must be at least 8 characters long";
    if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter";
    if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter";
    if (!/[0-9]/.test(pwd)) return "Password must contain at least one number";
    if (!/[!@#$%^&*()_+\-=\[\]{};\':"\\|,.<>\/?`~]/.test(pwd)) return "Password must contain at least one special character";
    return null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/auth/login", { username, password });
      if (response.data.success) {
        const user = response.data.user;
        const token = response.data.token;

        if (token) {
          setAuthToken(token);
          recordSessionLogin();
        }
        localStorage.setItem("hm_user", JSON.stringify(user));

        // Check if user requires credential upgrade
        if (response.data.must_change_credentials) {
          setTempUser(user);
          setNewUsername((user.username || "").replace(/\s/g, ""));
          setShowUpgradeModal(true);
          toast.info("Security update required: Please set a new username and password.");
          return;
        }

        toast.success(`Welcome, ${user.username}!`);

        // Redirect based on role
        if (user.role === "admin" || user.role === "global") {
          localStorage.setItem("hm_admin_auth", "true");
          navigate("/admin/dashboard");
        } else {
          localStorage.removeItem("hm_admin_auth");
          navigate(`/team/dashboard?property=${user.property_id || propertyId}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeCredentials = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      toast.error("Please enter a new username");
      return;
    }
    if (/\s/.test(newUsername)) {
      toast.error("Username cannot contain spaces. Use letters, numbers, underscores, or hyphens.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    const pwdErr = validatePassword(newPassword);
    if (pwdErr) {
      toast.error(pwdErr);
      return;
    }

    setUpgradeLoading(true);
    try {
      const res = await api.post("/auth/upgrade-credentials", {
        new_username: newUsername.trim(),
        new_password: newPassword
      });

      if (res.data.success) {
        const updatedUser = res.data.user;
        if (res.data.token) {
          setAuthToken(res.data.token);
        }
        localStorage.setItem("hm_user", JSON.stringify(updatedUser));
        setShowUpgradeModal(false);
        toast.success(`Credentials updated! Welcome, ${updatedUser.username}!`);

        if (updatedUser.role === "admin" || updatedUser.role === "global") {
          localStorage.setItem("hm_admin_auth", "true");
          navigate("/admin/dashboard");
        } else {
          localStorage.removeItem("hm_admin_auth");
          navigate(`/team/dashboard?property=${updatedUser.property_id || propertyId}`);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update credentials");
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Password Checklist Helpers
  const hasMinLen = newPassword.length >= 8;
  const hasLower = /[a-z]/.test(newPassword);
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};\x27:"\\|,.<>/?`~]/.test(newPassword);


  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-sm border border-gray-100">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-[#264653]">Team Login</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Hidden Monkey Stays</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4" autoComplete="on">
            <div>
              <Input
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="staff-username-input"
              />
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pr-10"
                data-testid="staff-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#2A9D8F] hover:bg-[#218377]"
              disabled={loading}
              data-testid="staff-login-btn"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <Link
              to={`/forgot-password?return=/team/login&username=${username || ""}`}
              className="text-sm text-[#FFC107] hover:underline block"
              data-testid="staff-forgot-password"
            >
              Forgot Password?
            </Link>
            <a
              href={`/?property=${propertyId}`}
              className="text-sm text-gray-400 hover:text-gray-600 block"
            >
              ← Back to Guest App
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Mandatory Credential Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-md w-[95vw]" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-xl bg-[#2A9D8F]/10 text-[#2A9D8F]">
                <ShieldCheck className="w-5 h-5" />
              </span>
              <DialogTitle className="text-lg text-[#264653]">Security Update Required</DialogTitle>
            </div>
            <DialogDescription className="text-xs text-gray-500 pt-1">
              Your account credentials must be updated to comply with our updated security policy.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpgradeCredentials} className="space-y-3.5 mt-2">
            <div>
              <Label className="text-xs font-semibold">New Username (No spaces allowed)</Label>
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value.replace(/\s/g, ""))}
                placeholder="e.g. rahul_varanasi"
                className="mt-1"
                required
              />
              <p className="text-[11px] text-gray-400 mt-0.5">Letters, numbers, underscores, and hyphens only.</p>
            </div>

            <div>
              <Label className="text-xs font-semibold">New Strong Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter strong password"
                  className="pr-10"
                  required
                />
                <button type="button" onClick={() => setShowNewPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Confirm New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="pr-10"
                  required
                />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Checklist */}
            <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100 text-[11px] space-y-1">
              <p className="font-semibold text-gray-700 mb-1">Password Requirements:</p>
              <div className="flex items-center gap-1.5">
                {hasMinLen ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
                <span className={hasMinLen ? "text-emerald-700 font-medium" : "text-gray-500"}>At least 8 characters</span>
              </div>
              <div className="flex items-center gap-1.5">
                {hasUpper ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
                <span className={hasUpper ? "text-emerald-700 font-medium" : "text-gray-500"}>At least 1 uppercase letter</span>
              </div>
              <div className="flex items-center gap-1.5">
                {hasLower ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
                <span className={hasLower ? "text-emerald-700 font-medium" : "text-gray-500"}>At least 1 lowercase letter</span>
              </div>
              <div className="flex items-center gap-1.5">
                {hasNumber ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
                <span className={hasNumber ? "text-emerald-700 font-medium" : "text-gray-500"}>At least 1 number</span>
              </div>
              <div className="flex items-center gap-1.5">
                {hasSpecial ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <X className="w-3.5 h-3.5 text-gray-300" />}
                <span className={hasSpecial ? "text-emerald-700 font-medium" : "text-gray-500"}>At least 1 special character (!@#$%^&* etc.)</span>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2A9D8F] hover:bg-[#218377] text-white font-semibold mt-2"
              disabled={upgradeLoading}
            >
              {upgradeLoading ? "Updating..." : "Save Credentials & Continue"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
